// screens/staff/SegreteriaFrequenzeScreen.tsx
//
// W6 v1 Session 1 — Segreteria attendance recording + history.
//
// Two views in one screen:
//   - Default: list of recent attendance entries (most recent at top), with
//     a "Registra frequenza" CTA.
//   - Drilldown: when a student is selected, show that student's attendance
//     history.

import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useTokens } from '../../state/theme';
import { staffApi, type RosterStudent, type AttendanceRow } from '../../api/staff';
import { formatItalianExamDate } from '../../utils/exam-date';

type AttendanceKind = 'lezione_teoria' | 'guida' | 'simulazione_in_aula';

const KIND_LABEL: Record<AttendanceKind, string> = {
  lezione_teoria:        'Lezione di teoria',
  guida:                 'Guida',
  simulazione_in_aula:   'Simulazione in aula',
};

interface NewAttendanceFormState {
  studentId: string;
  date:      string;
  kind:      AttendanceKind;
  notes:     string;
}

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

export default function SegreteriaFrequenzeScreen(): JSX.Element {
  const tokens = useTokens();
  const [students, setStudents] = useState<RosterStudent[]>([]);
  const [drilldown, setDrilldown] = useState<RosterStudent | null>(null);
  const [drillRows, setDrillRows] = useState<AttendanceRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showRecord, setShowRecord] = useState(false);
  const [recordForm, setRecordForm] = useState<NewAttendanceFormState>({
    studentId: '',
    date:      todayIso(),
    kind:      'lezione_teoria',
    notes:     '',
  });
  const [recording, setRecording] = useState(false);

  async function refreshStudents() {
    setLoading(true);
    setError(null);
    try {
      const s = await staffApi.listStudents();
      setStudents(s.students);
    } catch {
      setError('Impossibile caricare gli studenti.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refreshStudents(); }, []);

  useEffect(() => {
    if (!drilldown) { setDrillRows(null); return; }
    let alive = true;
    staffApi.listAttendance(drilldown.id).then((res) => {
      if (alive) setDrillRows(res.attendance);
    }).catch(() => { if (alive) setError('Impossibile caricare le presenze.'); });
    return () => { alive = false; };
  }, [drilldown]);

  async function handleRecord(e: React.FormEvent) {
    e.preventDefault();
    if (!recordForm.studentId) return;
    setRecording(true);
    try {
      await staffApi.recordAttendance(recordForm.studentId, {
        date:  recordForm.date,
        kind:  recordForm.kind,
        notes: recordForm.notes.trim() || undefined,
      });
      // If we were drilled into the same student, refresh their history.
      if (drilldown && drilldown.id === recordForm.studentId) {
        const res = await staffApi.listAttendance(drilldown.id);
        setDrillRows(res.attendance);
      }
      setShowRecord(false);
      setRecordForm({
        studentId: '',
        date:      todayIso(),
        kind:      'lezione_teoria',
        notes:     '',
      });
    } catch {
      setError('Impossibile registrare la frequenza.');
    } finally {
      setRecording(false);
    }
  }

  const studentLookup = useMemo(() => {
    const m = new Map<string, RosterStudent>();
    students.forEach(s => m.set(s.id, s));
    return m;
  }, [students]);

  return (
    <section data-testid="segreteria-frequenze-screen">
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: tokens.fonts.serif, fontSize: 26, fontWeight: 700, margin: 0 }}>
            {drilldown ? `Frequenze di ${drilldown.first_name}` : 'Frequenze'}
          </h1>
          <p style={{ color: tokens.textMuted, margin: '4px 0 0 0', fontSize: 13 }}>
            Registri lezioni di teoria, guide e simulazioni in aula.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {drilldown && (
            <button onClick={() => setDrilldown(null)} data-testid="segreteria-attendance-back" style={{ background: 'transparent', color: tokens.textMuted, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.pill, padding: '10px 18px', cursor: 'pointer', fontSize: 14 }}>
              ← Indietro
            </button>
          )}
          <button
            onClick={() => {
              setRecordForm({ studentId: drilldown?.id ?? '', date: todayIso(), kind: 'lezione_teoria', notes: '' });
              setShowRecord(true);
            }}
            data-testid="segreteria-record-attendance"
            style={{ background: tokens.accent, color: tokens.mode === 'dark' ? '#1a1510' : '#fff', border: 'none', borderRadius: tokens.radius.pill, padding: '10px 20px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
          >
            + Registra frequenza
          </button>
        </div>
      </header>

      {error && <div role="alert" style={{ marginBottom: 16, color: tokens.wrong, fontSize: 13 }}>{error}</div>}

      {drilldown ? (
        <div data-testid="segreteria-attendance-drilldown">
          {drillRows === null ? (
            <div style={{ color: tokens.textMuted, fontSize: 14 }}>Caricamento…</div>
          ) : drillRows.length === 0 ? (
            <div data-testid="segreteria-attendance-empty" style={{ color: tokens.textMuted, fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
              Nessuna frequenza registrata per {drilldown.first_name}.
            </div>
          ) : (
            <div role="table" aria-label="Frequenze studente" style={{ width: '100%', border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, overflow: 'hidden' }}>
              <div role="row" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 2fr', padding: '10px 14px', background: tokens.surface, fontSize: 12, color: tokens.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                <span>Data</span>
                <span>Tipo</span>
                <span>Note</span>
              </div>
              {drillRows.map((row) => (
                <div key={row.id} role="row" data-testid={`segreteria-attendance-row-${row.id}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 2fr', padding: '12px 14px', borderTop: `1px solid ${tokens.line}`, fontSize: 14, alignItems: 'center' }}>
                  <span>{new Date(row.date).toLocaleDateString('it-IT')}</span>
                  <span>{KIND_LABEL[row.kind]}</span>
                  <span style={{ color: tokens.textMuted, fontSize: 13 }}>{row.notes ?? '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : loading ? (
        <div data-testid="segreteria-frequenze-loading" style={{ color: tokens.textMuted, fontSize: 14 }}>Caricamento…</div>
      ) : students.length === 0 ? (
        <div data-testid="segreteria-frequenze-empty" style={{ color: tokens.textMuted, fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
          Non ci sono ancora studenti iscritti. Iscriva uno studente da "Studenti" per iniziare a registrare le frequenze.
        </div>
      ) : (
        <div role="table" aria-label="Studenti — apri per vedere le frequenze" style={{ width: '100%', border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, overflow: 'hidden' }}>
          <div role="row" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 0.7fr', padding: '10px 14px', background: tokens.surface, fontSize: 12, color: tokens.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            <span>Nome</span>
            <span>Classe</span>
            <span>Esame</span>
            <span></span>
          </div>
          {students.filter(s => !s.paused).map((s) => (
            <div key={s.id} role="row" data-testid={`segreteria-frequenze-student-${s.id}`} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 0.7fr', padding: '12px 14px', borderTop: `1px solid ${tokens.line}`, fontSize: 14, alignItems: 'center' }}>
              <span>{s.first_name}</span>
              <span style={{ color: tokens.textMuted, fontSize: 13 }}>{s.cohort_name ?? '—'}</span>
              <span style={{ color: tokens.textMuted, fontSize: 13 }}>{formatItalianExamDate(s.exam_date) ?? '—'}</span>
              <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setDrilldown(s)} data-testid={`segreteria-attendance-open-${s.id}`} style={{ background: 'transparent', color: tokens.accentInk, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.sm, padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}>
                  Apri
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {showRecord && (
        <ModalShell title="Registra frequenza" onClose={() => setShowRecord(false)} testid="segreteria-record-modal">
          <form onSubmit={handleRecord} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <FieldLabel tokens={tokens} label="Studente">
              <select value={recordForm.studentId} onChange={(e) => setRecordForm({ ...recordForm, studentId: e.target.value })} required style={inputStyle(tokens)} data-testid="segreteria-record-student">
                <option value="">Selezioni uno studente…</option>
                {students.filter(s => !s.paused).map(s => <option key={s.id} value={s.id}>{s.first_name}{s.cohort_name ? ` · ${s.cohort_name}` : ''}</option>)}
              </select>
            </FieldLabel>
            <FieldLabel tokens={tokens} label="Data">
              <input type="date" value={recordForm.date} onChange={(e) => setRecordForm({ ...recordForm, date: e.target.value })} required style={inputStyle(tokens)} data-testid="segreteria-record-date" />
            </FieldLabel>
            <FieldLabel tokens={tokens} label="Tipo">
              <select value={recordForm.kind} onChange={(e) => setRecordForm({ ...recordForm, kind: e.target.value as AttendanceKind })} style={inputStyle(tokens)} data-testid="segreteria-record-kind">
                <option value="lezione_teoria">Lezione di teoria</option>
                <option value="guida">Guida</option>
                <option value="simulazione_in_aula">Simulazione in aula</option>
              </select>
            </FieldLabel>
            <FieldLabel tokens={tokens} label="Note (opzionale)">
              <textarea value={recordForm.notes} onChange={(e) => setRecordForm({ ...recordForm, notes: e.target.value })} maxLength={500} rows={2} style={{ ...inputStyle(tokens), resize: 'vertical' }} data-testid="segreteria-record-notes" />
            </FieldLabel>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" onClick={() => setShowRecord(false)} style={btnSecondary(tokens)}>Annulla</button>
              <button type="submit" disabled={recording || !recordForm.studentId} style={btnPrimary(tokens)} data-testid="segreteria-record-submit">
                {recording ? 'Salvataggio…' : 'Registra'}
              </button>
            </div>
          </form>
          {recordForm.studentId && studentLookup.get(recordForm.studentId) && (
            <p style={{ marginTop: 12, fontSize: 12, color: tokens.textMuted }}>
              Frequenza per {studentLookup.get(recordForm.studentId)!.first_name}.
            </p>
          )}
        </ModalShell>
      )}
    </section>
  );
}

function inputStyle(tokens: ReturnType<typeof useTokens>): React.CSSProperties {
  return { background: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, padding: '10px 14px', color: tokens.text, fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: tokens.fonts.body, outline: 'none' };
}
function btnPrimary(tokens: ReturnType<typeof useTokens>): React.CSSProperties {
  return { background: tokens.accent, color: tokens.mode === 'dark' ? '#1a1510' : '#fff', border: 'none', borderRadius: tokens.radius.pill, padding: '10px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600 };
}
function btnSecondary(tokens: ReturnType<typeof useTokens>): React.CSSProperties {
  return { background: 'transparent', color: tokens.textMuted, border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.pill, padding: '10px 18px', cursor: 'pointer', fontSize: 14 };
}
function FieldLabel({ tokens, label, children }: { tokens: ReturnType<typeof useTokens>; label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: tokens.textMuted }}>
      <span>{label}</span>
      {children}
    </label>
  );
}
// Stable id counter so multiple ModalShells on the screen each have a unique
// heading id for `aria-labelledby` (e.g. record-modal AND a hypothetical
// confirm-modal). Component-scoped — no global registry needed.
let modalShellSeq = 0;

function ModalShell({ title, onClose, children, testid }: { title: string; onClose: () => void; children: React.ReactNode; testid?: string }): JSX.Element {
  const tokens = useTokens();
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // W6 v1.1 Session 3 (polish item 10) — `aria-labelledby` over `aria-label`
  // mirrors the LacuneQueueScreen ModalShell pattern (`aria-labelledby=
  // "modal-respond-title"`) so screen readers announce the visible heading
  // text rather than a parallel `aria-label` string that could drift out of
  // sync. Stable per-instance id via a module-level counter.
  const headingIdRef = useRef<string>(`frequenze-modal-title-${++modalShellSeq}`);

  useEffect(() => {
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null;
    const node = containerRef.current;
    if (node) {
      const first = node.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      (first ?? node).focus();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [onClose]);

  return (
    <div data-testid={testid} role="dialog" aria-modal="true" aria-labelledby={headingIdRef.current}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* TODO v1.2: add Tab focus-trap so keyboard focus can't leak to underlying page. */}
      <div ref={containerRef} tabIndex={-1} style={{ background: tokens.bg, borderRadius: tokens.radius.lg, padding: 24, minWidth: 420, maxWidth: 520, border: `1px solid ${tokens.line}`, outline: 'none' }}>
        <h2 id={headingIdRef.current} style={{ fontFamily: tokens.fonts.serif, fontSize: 20, fontWeight: 700, margin: '0 0 16px 0' }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
